import Vue from 'vue'
import { getValidStandardSets, getItem } from '@/api/pathways'
import { getSuperPage } from '@/api/superpages'
import { getGoalByUuid, getGoalBySlug, getStaircaseForContent, getGoalCustomization, getSuperPageCustomization } from '@/api/goaldetail'
import { getStrategiesForUuids } from '@/api/strategies'
import { getToolkitStandards } from '@/api/toolkitstandards'
import { getResources } from '@/api/pathways'
import {
  blurContentPermissions,
  actionPermissions,
  CONTENT_TYPES,
  ACTION_TYPES,
} from '@/util/access_visibility'
import { isGoalPremium } from '@/util/goal_scores'
import { hasNonEnglishLang } from '@/util/resource_languages'

export default {
  namespaced: true,
  state: {
    fetching: false,
    fetchingCustomization: false,
    customizationError: null,
    content: {},
    customizedGoal: null,
    fetchedStandardsBySetCode: {},
    staircaseContent: [],
    strategies: [],
    items: {}, // uuid -> item document
    validStandardSets: null,
    saveToLibraryStatus: {
      savedErrorMessage: null,
      savedSuccessInfo: {
        name: null,
        uuid: null,
      },
    },
    forbidden: null,
    error: null,
    resources: {}, // Map of objectId -> resources array
    selectedLanguage: {}, // Map of objectId -> selected language
    resourcesFetching: {}, // Map of objectId -> fetching status
    autoReviewCompleted: false, // Track if auto-review has been run for current goal
  },
  mutations: {
    pageContentChanged(state) {
      state.strategies = []
      state.fetchedStandardsBySetCode = {}
      state.staircaseContent = []
      state.items = {}
      state.saveToLibraryStatus = {
        savedErrorMessage: null,
        savedSuccessInfo: {
          name: null,
          uuid: null,
        },
      }
      state.customizedGoal = null
      state.autoReviewCompleted = false
    },
    clearContent(state) {
      state.content = {}
      state.fetchingCustomization = false
      state.customizationError = null
      state.customizedGoal = null
      state.strategies = []
      state.fetchedStandardsBySetCode = {}
      state.staircaseContent = []
      state.items = {}
      state.resources = {}
      state.selectedLanguage = {}
      state.resourcesFetching = {}
      state.autoReviewCompleted = false
    },
    setFetching(state, flag) {
      state.fetching = flag
    },
    setFetchingCustomization(state, flag) {
      state.fetchingCustomization = flag
    },
    setCustomizationError(state, error) {
      state.customizationError = error
    },
    setCustomizedGoal(state, flag) {
      state.customizedGoal = flag
    },
    setPageContent(state, pageContent) {
      let contentCopy = pageContent
      if (state.content === pageContent) {
        // do a clone to create new object references
        // in order to guarantee reactive updates
        contentCopy = JSON.parse(JSON.stringify(pageContent))
      }
      state.content = contentCopy
    },
    applyCustomization(state, customizedContent) {
      if (customizedContent.goal_text) {
        Vue.set(state.content, 'goal_text', customizedContent.goal_text)
      }
      if (customizedContent.objectives && state.content.objectives) {
        customizedContent.objectives.forEach(customizedObj => {
          const existingObj = state.content.objectives.find(o => o.level === customizedObj.level)
          if (existingObj && customizedObj.text) {
            Vue.set(existingObj, 'text', customizedObj.text)
          }
        })
      }
      if (customizedContent.customized !== undefined) {
        Vue.set(state.content, 'customized', customizedContent.customized)
      }
    },
    setFetchedStandardsForSetCode(state, { setCode, standards }) {
      Vue.set(state.fetchedStandardsBySetCode, setCode, standards)
    },
    setStaircaseContent(state, staircaseContent) {
      state.staircaseContent = staircaseContent
    },
    setPageStrategies(state, strategiesArr) {
      state.strategies = strategiesArr
    },
    addItem(state, item) {
      let itemToAugment = {}
      itemToAugment[item.uuid] = item
      state.items = Object.assign({}, state.items, itemToAugment)
    },
    setValidStandardSets(state, standardSetCodes) {
      state.validStandardSets = standardSetCodes
    },
    setForbidden(state, forbidden) {
      state.forbidden = forbidden
    },
    setError(state, error) {
      state.error = error
    },
    // Expect payload to have name and uuid fields
    setSaveToLibrarySuccess(state, payload) {
      state.saveToLibraryStatus.savedSuccessInfo.name = payload.name
      state.saveToLibraryStatus.savedSuccessInfo.uuid = payload.uuid
    },
    setSaveToLibraryError(state, errorMessage) {
      state.saveToLibraryStatus.savedErrorMessage = errorMessage
    },
    resetSaveStatus(state) {
      state.saveToLibraryStatus = {
        savedErrorMessage: null,
        savedSuccessInfo: {
          name: null,
          uuid: null,
          libraryContentUrl: null,
        },
      }
    },
    setResources(state, { contentId, resources }) {
      // Make sure we're creating a new array reference to ensure reactivity
      Vue.set(state.resources, contentId, [...resources])
    },
    setResourcesFetching(state, { contentId, fetching }) {
      Vue.set(state.resourcesFetching, contentId, fetching)
    },
    setSelectedLanguage(state, { contentId, language }) {
      Vue.set(state.selectedLanguage, contentId, language)
    },
    setAutoReviewCompleted(state, completed) {
      state.autoReviewCompleted = completed
    }
  },
  actions: {
    // payload should have the following fields:
    // - type: CONTENT_TYPES.GOAL or CONTENT_TYPES.SUPER_PAGE
    // - attribute: uuid or slug
    // - value: the value corresponding to the attribute
    async fetchPageContent(context, payload) {
      let fetchFunc = null
      if (payload.type === CONTENT_TYPES.GOAL) {
        if (payload.attribute === 'uuid') {
          fetchFunc = getGoalByUuid
        } else {
          fetchFunc = getGoalBySlug
        }
      } else if (payload.type === CONTENT_TYPES.SUPER_PAGE) {
        if (payload.attribute === 'uuid') {
          fetchFunc = getSuperPage
        } else {
          // no op
          console.warn('Fetching anchor page by anything other than uuid is not supported')
        }
      }
      context.commit('setFetching', true)

      // Add skip_customization to params if content_profile is provided
      // this allows us to fetch the base content without customization
      // for a better loading experience since customizing takes time
      const params = { ...payload.params }
      if (params.content_profile) {
        params.skip_customization = 'true'
      }

      try {
        let goaldetail = await fetchFunc(payload.value, params)
        context.commit('setPageContent', goaldetail)
        context.commit('pageContentChanged')
        context.dispatch('fetchStrategiesForUuids', goaldetail.adaptation_strategies)
        context.commit('setFetching', false)

        // Phase 2: Fetch customization if needed
        if (goaldetail.should_customize && payload.params && payload.params.content_profile) {
          context.commit('setFetchingCustomization', true)
          context.commit('setCustomizedGoal', true)
          context.dispatch('fetchCustomization', {
            type: payload.type,
            uuid: goaldetail.uuid,
            contentProfile: payload.params.content_profile,
            betaMode: payload.params.beta_mode
          })
        } else if (params.content_profile) {
          context.commit('setFetchingCustomization', false)
        }

        return goaldetail
      } catch ({ status }) {
        if (status === 403) {
          context.commit('setForbidden', true)
          context.commit('setFetching', false)
          context.commit('setFetchingCustomization', false)
        } else {
          if (payload.retried) {
            context.commit('setError', true)
            context.commit('setFetching', false)
            context.commit('setFetchingCustomization', false)
          } else {
            payload.retried = true
            context.commit('setFetching', false)
            context.dispatch('fetchPageContent', payload)
          }
        }
      }
    },
    async fetchCustomization(context, { type, uuid, contentProfile, betaMode }) {
      context.commit('setFetchingCustomization', true)
      context.commit('setCustomizationError', null)

      try {
        const fetchFunc = type === CONTENT_TYPES.SUPER_PAGE ? getSuperPageCustomization : getGoalCustomization
        const customizedContent = await fetchFunc(uuid, contentProfile, betaMode)
        if (context.state.content && context.state.content.uuid === uuid) {
          context.commit('applyCustomization', customizedContent)
        }
      } catch (error) {
        console.error('Failed to fetch customization:', error)
        context.commit('setCustomizationError', error)
      } finally {
        if (context.state.content && context.state.content.uuid === uuid) {
          context.commit('setFetchingCustomization', false)
        }
      }
    },
    fetchPageStandardsForSets(context, { contentStandardSetRefs, standardSets }) {
      let standardSetRefs = context.state.content.standard_set_refs
      let fetchPromises = []

      standardSets.forEach((setCode) => {
        let standardRefsForSetCode = standardSetRefs
          .filter((setRef) => setRef.standard_set_code === setCode)
          .map((setRef) => setRef.standard_long_ref)

        if (standardRefsForSetCode.length > 0) {
          let fetchStandardsPromise = getToolkitStandards(setCode, standardRefsForSetCode, {
            substandards: true,
          }).then((results) => {
            let standards = results.standards.filter((standard) => {
              return !!standard
            })
            context.commit('setFetchedStandardsForSetCode', {
              setCode,
              standards,
            })
          })
          fetchPromises.push(fetchStandardsPromise)
        }
      })

      return Promise.all(fetchPromises)
    },
    async fetchPageStaircaseContentForSet(
      { state, commit },
      { contentUuid, staircaseStandardSetCode, subject }
    ) {
      if (!staircaseStandardSetCode) {
        return []
      }
      let staircaseContent = await getStaircaseForContent(
        contentUuid,
        staircaseStandardSetCode,
        subject
      )
      commit('setStaircaseContent', staircaseContent)
      return staircaseContent
    },
    fetchStrategiesForUuids(context, uuids) {
      return getStrategiesForUuids(uuids)
        .then((strategiesArr) => {
          context.commit('setPageStrategies', strategiesArr)
          return strategiesArr
        })
        .catch((err) => {
          console.dir(err)
        })
    },
    fetchItem(context, itemId) {
      if (context.state.items[itemId]) {
        return new Promise((resolve) => resolve(context.state.items[itemId]))
      } else {
        return getItem(itemId).then((item) => {
          context.commit('addItem', item)
          return item
        })
      }
    },
    async fetchValidStandardSets(context) {
      if (!context.state.validStandardSets) {
        let standardSetCodes = await getValidStandardSets()
        context.commit('setValidStandardSets', standardSetCodes)
        return standardSetCodes
      } else {
        return context.state.validStandardSets
      }
    },
    async fetchResources({ commit, state }, { contentId, resourceUuids }) {
      if (!resourceUuids || resourceUuids.length === 0) {
        commit('setResources', { contentId, resources: [] })
        return []
      }

      if (state.resources[contentId] &&
          state.resources[contentId].length === resourceUuids.length &&
          state.resources[contentId].every(r => resourceUuids.includes(r.uuid))) {
        return state.resources[contentId]
      }

      try {
        commit('setResourcesFetching', { contentId, fetching: true })
        const resources = await getResources(resourceUuids.join(','))
        commit('setResources', { contentId, resources: [...resources] })

        if (resources.length > 0 && !state.selectedLanguage[contentId]) {
          const languages = [...new Set(resources.map(r => r.language).filter(Boolean))]
          if (languages.length > 0) {
            if (languages.includes('english')) {
              commit('setSelectedLanguage', { contentId, language: 'english' })
            } else {
              commit('setSelectedLanguage', { contentId, language: languages[0] })
            }
          }
        }

        return resources
      } catch (error) {
        console.error('Error fetching resources:', error)
        return []
      } finally {
        commit('setResourcesFetching', { contentId, fetching: false })
      }
    },
    async fetchAllResources({ state, dispatch }) {
      if (state.content.goal_resources && state.content.goal_resources.length > 0) {
        await dispatch('fetchResources', {
          contentId: state.content.uuid,
          resourceUuids: state.content.goal_resources
        })
      }

      if (state.content.objectives) {
        await Promise.all(
          state.content.objectives
            .filter(objective => objective.resources && objective.resources.length > 0)
            .map(async objective => {
              return await dispatch('fetchResources', {
                contentId: objective.uuid,
                resourceUuids: objective.resources
              })
            })
        )
      }
    }
  },
  getters: {
    isAnchorPage(state) {
      return state.content.super_page
    },
    itemById(state) {
      return (itemId) => state.items[itemId]
    },
    customizedGoal(state) {
      return state.customizedGoal
    },
    strategyMap(state) {
      let strategyMap = {}
      state.strategies.forEach((strategy) => {
        if (strategy.published === true) {
          strategyMap[strategy.title.toLowerCase()] = strategy
        }
      })
      return strategyMap
    },
    visibleGroupedStandardsBySetCode(state, getters, rootState, rootGetters) {
      if (!state.content.subject || !state.content.standard_set_refs) {
        return []
      }
      let orderedStandardsBySet = []
      let fetchedStandardsBySetCode = state.fetchedStandardsBySetCode
      const distinctVisibleStandardSets = new Set()
      state.content.subject_alignments &&
        state.content.subject_alignments.forEach((sa) => {
          const standardSetsForSubject = rootGetters[
            'contentProfile/visibleStandardSetsForSubject'
          ](sa.subject, {})
          standardSetsForSubject.forEach((s) => distinctVisibleStandardSets.add(s))
        })

      distinctVisibleStandardSets.forEach((setCode) => {
        if (fetchedStandardsBySetCode[setCode] && fetchedStandardsBySetCode[setCode].length > 0) {
          orderedStandardsBySet.push({
            standardSetCode: setCode,
            standards: fetchedStandardsBySetCode[setCode],
          })
        }
      })

      return orderedStandardsBySet
    },
    udlAdaptations(state) {
      return (
        (state.content.udl_adaptations && [
          {
            type: 'engagement',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL III ') > -1
            }),
          },
          {
            type: 'representation',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL I ') > -1
            }),
          },
          {
            type: 'action & expression',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL II ') > -1
            }),
          },
        ]) ||
        []
      )
    },
    accessInfo(state, getters, rootState, rootGetters) {
      return blurContentPermissions(
        getters.isAnchorPage ? CONTENT_TYPES.SUPER_PAGE : CONTENT_TYPES.GOAL,
        state.content,
        rootGetters['user/authInfo']
      )
    },
    actionPermissions(state, getters, rootState, rootGetters) {
      return {
        save: actionPermissions(
          ACTION_TYPES.SAVE,
          getters.isAnchorPage ? CONTENT_TYPES.SUPER_PAGE : CONTENT_TYPES.GOAL,
          state.content,
          rootGetters['user/authInfo']
        ),
        printAssessment: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SUPER_PAGE_ASSESSMENT,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        printSavedAnchor: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SAVED_SUPER_PAGE,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        printSavedGoal: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SAVED_GOAL,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        shareSavedGoal: actionPermissions(
          ACTION_TYPES.SHARE,
          CONTENT_TYPES.SAVED_GOAL,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
      }
    },
    getPMAs(state) {
      return state?.content?.goal_resources || []
    },
    premiumBlock({ forbidden, content }, { isAnchorPage }, rootState, rootGetters) {
      const goalIsPremium = isGoalPremium({ super_page: isAnchorPage, score: content.score })
      return forbidden || (goalIsPremium && !rootGetters['user/authInfo'].isPremium)
    },
    goalLoadError(state) {
      return state.error || false
    },
    resourcesById: (state) => (contentId) => {
      return state.resources[contentId] || []
    },
    resourcesFetchingById: (state) => (contentId) => {
      return state.resourcesFetching[contentId] || false
    },
    selectedLanguageById: (state) => (contentId) => {
      return state.selectedLanguage[contentId] || ''
    },
    languagesById: (state) => (contentId) => {
      const resources = state.resources[contentId] || []
      return [...new Set(resources.map(r => r.language).filter(Boolean))].sort()
    },
    hasNonEnglishLangById: (state) => (contentId) => {
      const resources = state.resources[contentId] || []
      return hasNonEnglishLang(resources)
    },
    filteredResourcesById: (state, getters) => (contentId) => {
      const resources = state.resources[contentId] || []
      const selectedLanguage = getters.selectedLanguageById(contentId)

      if (!selectedLanguage) {
        return resources
      }

      return resources.filter(r => r.language === selectedLanguage || !r.language)
    },
    autoReviewCompleted(state) {
      return state.autoReviewCompleted
    },
  },
}
